import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { EnvironmentId, ThreadId } from "@t3tools/contracts";

import { loadOlderThreadMessages } from "../../environments/runtime/service";
import type { MessageWindowState } from "../../store";
import type { ChatMessage } from "../../types";
import { nextMessageLinkAction } from "./useMessageLink.logic";
import type { TimelineScrollHandle } from "./useTimelineScroll";

export function useMessageLink(input: {
  environmentId: EnvironmentId;
  threadId: ThreadId;
  messageId: string | undefined;
  messages: readonly ChatMessage[] | undefined;
  window: MessageWindowState | undefined;
  timelineRef: RefObject<TimelineScrollHandle | null>;
}) {
  const { environmentId, threadId, messages, window, timelineRef } = input;
  const scope = JSON.stringify([environmentId, threadId, input.messageId]);
  const [selection, setSelection] = useState<{
    scope: string;
    messageId: string;
    request: number;
  } | null>(null);
  const messageId = selection?.scope === scope ? selection.messageId : input.messageId;
  const request = selection?.scope === scope ? selection.request : 0;
  useEffect(() => setSelection(null), [scope]);
  const selectMessage = useCallback(
    (id: string) => {
      setSelection((previous) => ({ scope, messageId: id, request: (previous?.request ?? 0) + 1 }));
    },
    [scope],
  );
  const progressRef = useRef({ key: "", completed: false, cursor: "" });

  useEffect(() => {
    const key = JSON.stringify([environmentId, threadId, messageId, request]);
    if (progressRef.current.key !== key) {
      progressRef.current = { key, completed: false, cursor: "" };
    }
    const progress = progressRef.current;
    const action = nextMessageLinkAction(progress, messageId, messages, window);
    if (action === "scroll" && messageId) {
      const frame = requestAnimationFrame(() => {
        if (!timelineRef.current) return;
        timelineRef.current.scrollToMessage(messageId);
        progress.completed = true;
      });
      return () => cancelAnimationFrame(frame);
    }
    if (action === "load") loadOlderThreadMessages(environmentId, threadId);
  }, [environmentId, threadId, messageId, request, messages, window, timelineRef]);
  return selectMessage;
}
